import { StarIcon } from "@/components/icons/star.icon";

/**
 * Star rating row that supports fractional ratings.
 *
 * Renders `totalStars` icons (default 5). Each star's fill is the clamped
 * fraction `rating - index` in [0, 1]:
 *   - fraction 1   -> solid filled star (uses `className`)
 *   - fraction 0   -> faded star (uses `emptyClassName`, defaults to
 *                     `${className}/30`)
 *   - 0 < f < 1    -> faded base star with a clipped solid star overlay
 *                     covering `f * 100%` of the width
 */
export const StarRating = ({
  rating,
  totalStars = 5,
  className = "fill-primary",
  emptyClassName,
  width = 29,
  height = 29,
}: StarRatingProps) => {
  const safeRating = Number.isFinite(rating) ? Math.max(0, rating) : 0;
  const fadedClassName = emptyClassName ?? `${className}/30`;

  return Array.from({ length: totalStars }).map((_, index) => {
    const fraction = Math.min(1, Math.max(0, safeRating - index));

    if (fraction >= 1) {
      return (
        <StarIcon
          key={index}
          width={width}
          height={height}
          className={`${className} shrink-0`}
        />
      );
    }

    if (fraction <= 0) {
      return (
        <StarIcon
          key={index}
          width={width}
          height={height}
          className={`${fadedClassName} shrink-0`}
        />
      );
    }

    return (
      <span
        key={index}
        className="relative inline-block shrink-0"
        style={{ width, height }}>
        <StarIcon
          width={width}
          height={height}
          className={`${fadedClassName} shrink-0`}
        />
        <span
          className="absolute inset-y-0 left-0 overflow-hidden"
          style={{ width: `${fraction * 100}%` }}>
          <StarIcon
            width={width}
            height={height}
            className={`${className} shrink-0`}
          />
        </span>
      </span>
    );
  });
};

type StarRatingProps = {
  /** Rating value, can be fractional (e.g. 4.6). */
  rating: number;
  /** Total number of stars to render. Defaults to 5. */
  totalStars?: number;
  /** Tailwind class applied to filled stars (e.g. `fill-primary`). */
  className?: string;
  /** Tailwind class applied to empty / partially-empty stars. Defaults to `${className}/30`. */
  emptyClassName?: string;
  width?: number;
  height?: number;
};
